import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'next-i18next';
import { useAppContext } from '../../../src/store/AppContext';
import displayPrice from '../../../src/utils/displayPrice';
import useStocks from '../../../src/hooks/useStocks';
import usePrice from '../../../src/hooks/usePrice';
import useBookingCartValidation from '../../../src/hooks/useBookingCartValidation';
import useApplyServerCartPrices from '../../../src/hooks/useApplyServerCartPrices';

// Icons
import CloseIcon from '../../../public/icons/cross.svg';

// Components
import PlayerEntry from './PlayerEntry/PlayerEntry';
import PlayersCounter from './PlayersCounter/PlayersCounter';
import Hosting from './Hosting/Hosting';
import NextButton from './NextButton/NextButton';
import CartInformations from './CartInformations/CartInformations';
import CartAlert from './CartAlert/CartAlert';

import styles from './Cart.module.scss';

function Cart({
  reverseBackground, setCartIsOpened, isOutsideTunnel,
}) {
  const { t } = useTranslation();
  const [{ currentPlayer, players, hosting }] = useAppContext();

  const { getStockExtraByPlayer } = useStocks();
  const { getTotalCart } = usePrice();

  // 🎯 VALIDATION CENTRALISÉE
  const {
    validation,
    canProceedToNextStep,
    hasPriceChangeWarning,
    activePriceChanges,
    dismissPriceChange,
  } = useBookingCartValidation();

  // Apply latest server prices into local AppContext (shared with MobileCart)
  useApplyServerCartPrices();

  const cartContent = useRef('cartContent');

  const [needScroll, setNeedScroll] = useState(false);
  const [productsStockByPlayer, setProductsStockByPlayer] = useState(null);

  const handleScrollCheck = () => {
    if (cartContent?.current) {
      setNeedScroll(cartContent.current.scrollHeight > cartContent.current.clientHeight);
    }
  };

  useEffect(() => {
    handleScrollCheck();

    window.addEventListener('resize', handleScrollCheck, true);
    return () => window.removeEventListener('resize', handleScrollCheck, true);
  }, []);

  useEffect(() => {
    if (!productsStockByPlayer && players?.length) {
      getStockExtraByPlayer(setProductsStockByPlayer, players);
    }
  }, [players]);

  // 🎯 Calcul du prix (totalité du panier)
  const totalPrice = getTotalCart();

  // 🎯 Recalculer le total avec les nouveaux prix du serveur
  const getTotalWithServerPrices = () => {
    if (!players?.length && !hosting?.length) return 0;

    // Approach A: local state is synchronized with server via `updatePlayersInCart`.
    // The displayed total should therefore be the local total computed by `getTotalCart()`.
    return totalPrice;
  };

  // Gestion suppression d'un produit à prix changé :
  // On doit appeler dismissPriceChange(productId) lors de la suppression d'un produit à prix changé
  // Supposons que la suppression se fait dans PlayerEntry ou Hosting, il faut leur passer dismissPriceChange

  return (
    <section ref={cartContent} className={`${styles.cart_wrapper} ${reverseBackground ? styles.reverse : ''}`}>
      <div className={`${styles.sticky_top} flex a-center j-spb`}>
        <h2 className="fs-20 text-c-primary fw-700">{t('cart.title')}</h2>
        <PlayersCounter className={`${styles.counter} flex a-center ${setCartIsOpened ? 'm-l-20' : ''}`} />
        {setCartIsOpened ? (
          <button type="button" onClick={() => setCartIsOpened(false)} className="m-l-auto c-pointer">
            <CloseIcon width={16} height={16} />
          </button>
        ) : null}
      </div>

      {players?.length ? (
        <>
          <div className="m-b-10 p-r-40 p-l-40">
            {players.map((player) => (
              <PlayerEntry
                key={player.id}
                player={player}
                isCurrentPlayer={currentPlayer?.id && player.id === currentPlayer.id}
                handleScrollCheck={handleScrollCheck}
                optionsStocks={productsStockByPlayer?.find((stock) => stock.playerId === player.id)?.stockByExtra}
                isUnavailable={validation.unavailableProducts.includes(`stage_${player.id}_${player.stage?.packageId}`)}
                unavailableProducts={validation.unavailableProducts}
                activePriceChanges={activePriceChanges}
                dismissPriceChange={dismissPriceChange}
              />
            ))}
          </div>

          {hosting?.length ? (
            <div className="m-b-10 p-r-40 p-l-40">
              <Hosting
                handleScrollCheck={handleScrollCheck}
                dismissPriceChange={dismissPriceChange}
                activePriceChanges={activePriceChanges}
                unavailableProducts={validation.unavailableProducts}
              />
            </div>
          ) : null}

          <div
            className={`${styles.sticky_bottom} ${needScroll ? styles.need_scroll : ''}`}
          >
            <div className={`${styles.total} flex a-center j-spb p-20 m-b-20`}>
              <p className="fw-500 fs-20 p-r-20">{t('cart.total')}</p>
              <p className="fw-700 fs-20 ws-nowrap">
                {`${displayPrice(getTotalWithServerPrices())} €`}
              </p>
            </div>

            {/* 🎯 AFFICHAGE DES ALERTES - STOCK ET PRIX */}
            <div className="m-b-10">
              <CartAlert
                errors={validation.errors}
                hasPriceChangeWarning={hasPriceChangeWarning}
                activePriceChanges={activePriceChanges}
              />
            </div>

            <div className={styles.cta_btn}>
              <CartInformations />
              <NextButton
                isOutsideTunnel={isOutsideTunnel}
                canProceed={canProceedToNextStep()}
                hasValidationErrors={!canProceedToNextStep()}
              />
            </div>
          </div>
        </>
      ) : <p className="p-r-40 p-l-40 p-t-20">{t('cart.empty')}</p>}
    </section>
  );
}

Cart.propTypes = {
  reverseBackground: PropTypes.bool,
  setCartIsOpened: PropTypes.func,
  isOutsideTunnel: PropTypes.bool,
};

Cart.defaultProps = {
  reverseBackground: false,
  setCartIsOpened: null,
  isOutsideTunnel: false,
};

export default Cart;
